import { useState, useEffect, useRef, useCallback } from 'react';

const WS_URL = process.env.REACT_APP_WS_URL ||
  (process.env.NODE_ENV === 'production'
    ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`
    : 'ws://localhost:5000/ws');

const RECONNECT_DELAY = 3000;
const PING_INTERVAL = 30000;

export default function useWebSocket(authenticated) {
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const subscribersRef = useRef(new Map());

  const connect = useCallback(() => {
    if (!authenticated) return;

    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);

        // Authenticate
        ws.send(JSON.stringify({ type: 'auth' }));

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle specific message types
          if (message.type === 'auth_success') {
            console.log('WebSocket authenticated');
            return;
          }

          if (message.type === 'pong') {
            return;
          }

          // Notify subscribers
          const typeSubscribers = subscribersRef.current.get(message.type);
          if (typeSubscribers) {
            typeSubscribers.forEach(callback => callback(message));
          }

          // Also notify 'all' subscribers
          const allSubscribers = subscribersRef.current.get('all');
          if (allSubscribers) {
            allSubscribers.forEach(callback => callback(message));
          }

          // Add to message history (limit to last 100)
          setMessages(prev => [...prev.slice(-99), message]);
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);
        clearInterval(pingIntervalRef.current);

        // Reconnect after delay
        if (authenticated) {
          reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY);
    }
  }, [authenticated]);

  const disconnect = useCallback(() => {
    clearTimeout(reconnectTimeoutRef.current);
    clearInterval(pingIntervalRef.current);

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnected(false);
  }, []);

  const send = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  const subscribe = useCallback((type, callback) => {
    if (!subscribersRef.current.has(type)) {
      subscribersRef.current.set(type, new Set());
    }
    subscribersRef.current.get(type).add(callback);

    // Return unsubscribe function
    return () => {
      const typeSubscribers = subscribersRef.current.get(type);
      if (typeSubscribers) {
        typeSubscribers.delete(callback);
      }
    };
  }, []);

  const subscribeLogs = useCallback((serverName) => {
    send({ type: 'subscribe_logs', server: serverName });
  }, [send]);

  const unsubscribeLogs = useCallback((serverName) => {
    send({ type: 'unsubscribe_logs', server: serverName });
  }, [send]);

  useEffect(() => {
    if (authenticated) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [authenticated, connect, disconnect]);

  return {
    connected,
    messages,
    send,
    subscribe,
    subscribeLogs,
    unsubscribeLogs
  };
}
